import Chart, { useChart } from 'src/components/chart';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Box } from '@mui/system';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useRef, useState } from 'react';
import { generateAnnotations } from 'src/utils/graph-demo-annotations';
import { formatNumber } from 'src/utils/format-number';
import { formatCurrency } from 'src/utils/format-currency';

type Props = {
  series: {
    name: string;
    data: number[];
  }[];
  labels: string[];
  height?: number;

  /** Override the per-series fill colors. Falls back to the legacy palette when omitted. */
  colors?: string[];

  /** Top radius applied to the last stacked segment of each column. */
  barBorderRadius?: number;

  /** Render the value of each segment on the bars. */
  showDataLabels?: boolean;

  /** Diameter (px) of the round legend markers. */
  legendMarkerSize?: number;

  /** Override the legend label text color. */
  legendLabelColor?: string;

  /** Hide the built-in ApexCharts legend (use when rendering a custom legend outside). */
  hideLegend?: boolean;

  /** Format values as currency (using the workspace currency) instead of plain numbers. */
  currency?: boolean;

  /** Extra tooltip rows. */
  extraTooltipRows?: {
    name: string;
    color: string;
    compute: (
      values: number[],
      names: string[]
    ) => number;
  }[];
};

export default function ChartColumnStacked({
  series,
  labels,
  height = 350,
  colors,
  barBorderRadius = 0,
  showDataLabels = false,
  legendMarkerSize = 16,
  legendLabelColor,
  hideLegend = false,
  currency = false,
  extraTooltipRows,
}: Props) {
  const theme = useTheme();

  const filterStore = useSelector(
    (state: any) => state.filters
  );

  const workspace =
    useCurrentWorkspace();

  const chartRef = useRef(null);

  /**
   * Wrapper DOM du graphique.
   *
   * On l'utilise uniquement pour injecter
   * le petit point coloré directement
   * DANS le background du label.
   */
  const chartContainerRef =
    useRef<HTMLDivElement | null>(null);

  /**
   * Séries cachées depuis la légende.
   *
   * Contrairement aux dataLabels Apex,
   * nos annotations ne savent pas automatiquement
   * qu'une série est masquée.
   *
   * On synchronise donc nous-mêmes cet état.
   */
  const [
    hiddenSeries,
    setHiddenSeries,
  ] = useState<Set<string>>(
    () => new Set()
  );

  const annotations =
    generateAnnotations(
      workspace,
      filterStore,
      labels
    );

  const lang =
    localStorage.getItem(
      'i18nextLng'
    ) || 'en';

  // --------------------------------------------------------------------
  // Formatting
  // --------------------------------------------------------------------

  const formatValue = (
    val: number
  ) =>
    currency
      ? formatCurrency(
        val,
        workspace?.currency ||
        'USD',
        lang,
        true,
        1
      )
      : formatNumber(val);

  // --------------------------------------------------------------------
  // Colors
  // --------------------------------------------------------------------

  const resolvedColors =
    colors ??
    (
      series?.length === 3
        ? [
          theme?.palette
            .success.light,
          theme.palette
            .tertiary.light,
          '#b7b7b7',
        ]
        : series?.length === 5
          ? [
            theme.palette
              .success.light,
            '#1e88e5',
            '#64b5f6',
            '#b7dcfb',
            '#b7b7b7',
          ]
          : [
            '#118d57',
            '#1e88e5',
            '#64b5f6',
            '#E1E1E1',
          ]
    );

  // --------------------------------------------------------------------
  // Custom labels
  //
  // IMPORTANT :
  //
  // Comme le graphique est STACKED,
  // la position Y d'un label n'est pas simplement
  // serie.data[dataPointIndex].
  //
  // Exemple :
  //
  // Série 1 = 100
  // Série 2 = 50
  // Série 3 = 20
  //
  // Labels :
  //
  // Série 1 -> y = 100
  // Série 2 -> y = 150
  // Série 3 -> y = 170
  //
  // Et on ne compte PAS les séries cachées.
  // --------------------------------------------------------------------

  const labelAnnotations =
    showDataLabels
      ? series.flatMap(
        (
          serie,
          seriesIndex
        ) => {
          /**
           * Série cachée :
           * aucun label pour elle.
           */
          if (
            hiddenSeries.has(
              serie.name
            )
          ) {
            return [];
          }

          return serie.data
            .map(
              (
                value,
                dataPointIndex
              ) => {
                const numericValue =
                  Number(value);

                if (
                  !Number.isFinite(
                    numericValue
                  ) ||
                  numericValue <= 0
                ) {
                  return null;
                }

                // ----------------------------------------------
                // Position verticale cumulée
                // ----------------------------------------------

                let cumulativeValue = 0;

                for (
                  let i = 0;
                  i <= seriesIndex;
                  i += 1
                ) {
                  const currentSeries =
                    series[i];

                  /**
                   * Une série cachée ne fait plus
                   * partie visuellement du stack.
                   */
                  if (
                    hiddenSeries.has(
                      currentSeries.name
                    )
                  ) {
                    continue;
                  }

                  const currentValue =
                    Number(
                      currentSeries
                        .data[
                      dataPointIndex
                      ]
                    );

                  if (
                    Number.isFinite(
                      currentValue
                    ) &&
                    currentValue > 0
                  ) {
                    cumulativeValue +=
                      currentValue;
                  }
                }

                /**
                 * Même principe que sur ChartMixed :
                 * label juste au-dessus de son point.
                 *
                 * Ici le "point" correspond au sommet
                 * du segment empilé.
                 */
                const offsetY = 7;

                return {
                  x:
                    labels[
                    dataPointIndex
                    ],

                  y: cumulativeValue,

                  marker: {
                    size: 0,
                  },

                  label: {
                    text:
                      formatValue(
                        numericValue
                      ),

                    offsetY,

                    borderColor:
                      'rgba(145, 158, 171, 0.20)',

                    borderWidth: 1,

                    borderRadius: 4,

                    style: {
                      background:
                        '#FFFFFF',

                      color:
                        '#1C252E',

                      fontSize:
                        '11px',

                      fontWeight:
                        500,

                      fontFamily:
                        'Public Sans, sans-serif',

                      /**
                       * seriesIndex permet ensuite
                       * d'injecter la bonne couleur.
                       */
                      cssClass:
                        `chart-data-label chart-data-label-series-${seriesIndex}`,
                    },
                  },
                };
              }
            )
            .filter(Boolean);
        }
      )
      : [];

  // --------------------------------------------------------------------
  // Inject series-colored dot INSIDE each label
  // --------------------------------------------------------------------

  const injectSeriesDotsIntoLabels = () => {
    requestAnimationFrame(() => {
      const container = chartContainerRef.current;

      if (!container) return;

      // Nettoyage des points ajoutés lors d'un précédent rendu.
      container
        .querySelectorAll('.chart-label-dot')
        .forEach((dot) => dot.remove());

      series.forEach((serie, seriesIndex) => {
        if (hiddenSeries.has(serie.name)) {
          return;
        }

        const seriesColor =
          resolvedColors[seriesIndex] || '#637381';

        const textElements =
          container.querySelectorAll(
            `text.chart-data-label-series-${seriesIndex}`
          );

        textElements.forEach((node) => {
          const text = node as SVGTextElement;

          // ------------------------------------------------------------
          // Trouver le rect correspondant au label
          // ------------------------------------------------------------

          let rect =
            text.previousElementSibling as SVGRectElement | null;

          if (
            !rect ||
            rect.tagName.toLowerCase() !== 'rect'
          ) {
            const parent = text.parentElement;

            if (!parent) return;

            const siblings = Array.from(parent.children);
            const textIndex = siblings.indexOf(text);

            for (let i = textIndex - 1; i >= 0; i -= 1) {
              if (
                siblings[i].tagName.toLowerCase() === 'rect'
              ) {
                rect = siblings[i] as SVGRectElement;
                break;
              }
            }
          }

          if (!rect) return;

          // ------------------------------------------------------------
          // Valeurs du design
          //
          // padding: 4px 8px
          // gap: 4px
          // dot: 6px
          // ------------------------------------------------------------

          const PADDING_X = 8;
          const PADDING_Y = 4;
          const GAP = 4;

          const DOT_RADIUS = 3;
          const DOT_SIZE = DOT_RADIUS * 2;

          // ------------------------------------------------------------
          // Mesure réelle du texte SVG
          // ------------------------------------------------------------

          let textBBox: DOMRect | SVGRect;

          try {
            textBBox = text.getBBox();
          } catch {
            return;
          }

          const textWidth = textBBox.width;
          const textHeight = textBBox.height;

          // ------------------------------------------------------------
          // Dimensions exactes du chip
          //
          // 8px
          // + 6px point
          // + 4px gap
          // + largeur texte
          // + 8px
          // ------------------------------------------------------------

          const chipWidth =
            PADDING_X +
            DOT_SIZE +
            GAP +
            textWidth +
            PADDING_X;

          const chipHeight =
            PADDING_Y +
            textHeight +
            PADDING_Y;

          // ------------------------------------------------------------
          // Centre ORIGINAL du label Apex
          //
          // C'est ce centre qu'on conserve afin que le chip entier
          // soit centré sur la barre.
          // ------------------------------------------------------------

          const originalRectXAttr =
            rect.getAttribute('data-original-x');

          const originalRectWidthAttr =
            rect.getAttribute('data-original-width');

          const originalRectX =
            originalRectXAttr !== null
              ? Number(originalRectXAttr)
              : Number(rect.getAttribute('x') || 0);

          const originalRectWidth =
            originalRectWidthAttr !== null
              ? Number(originalRectWidthAttr)
              : Number(rect.getAttribute('width') || 0);

          if (originalRectXAttr === null) {
            rect.setAttribute(
              'data-original-x',
              String(originalRectX)
            );
          }

          if (originalRectWidthAttr === null) {
            rect.setAttribute(
              'data-original-width',
              String(originalRectWidth)
            );
          }

          const centerX =
            originalRectX +
            originalRectWidth / 2;

          // ------------------------------------------------------------
          // Centre vertical original
          // ------------------------------------------------------------

          const originalRectYAttr =
            rect.getAttribute('data-original-y');

          const originalRectHeightAttr =
            rect.getAttribute('data-original-height');

          const originalRectY =
            originalRectYAttr !== null
              ? Number(originalRectYAttr)
              : Number(rect.getAttribute('y') || 0);

          const originalRectHeight =
            originalRectHeightAttr !== null
              ? Number(originalRectHeightAttr)
              : Number(rect.getAttribute('height') || 0);

          if (originalRectYAttr === null) {
            rect.setAttribute(
              'data-original-y',
              String(originalRectY)
            );
          }

          if (originalRectHeightAttr === null) {
            rect.setAttribute(
              'data-original-height',
              String(originalRectHeight)
            );
          }

          const centerY =
            originalRectY +
            originalRectHeight / 2;

          // ------------------------------------------------------------
          // Nouveau rect
          //
          // Le CHIP COMPLET reste centré sur la barre.
          // ------------------------------------------------------------

          const chipX =
            centerX -
            chipWidth / 2;

          const chipY =
            centerY -
            chipHeight / 2;

          rect.setAttribute(
            'x',
            String(chipX)
          );

          rect.setAttribute(
            'y',
            String(chipY)
          );

          rect.setAttribute(
            'width',
            String(chipWidth)
          );

          rect.setAttribute(
            'height',
            String(chipHeight)
          );

          rect.setAttribute(
            'rx',
            '4'
          );

          rect.setAttribute(
            'ry',
            '4'
          );

          // ------------------------------------------------------------
          // Position du point
          //
          // | 8px | ● | 4px | texte | 8px |
          // ------------------------------------------------------------

          const dotCenterX =
            chipX +
            PADDING_X +
            DOT_RADIUS;

          const dotCenterY =
            centerY;

          // ------------------------------------------------------------
          // Position du texte
          //
          // Le texte commence exactement après :
          //
          // padding 8
          // + point 6
          // + gap 4
          // ------------------------------------------------------------

          const textStartX =
            chipX +
            PADDING_X +
            DOT_SIZE +
            GAP;

          /*
           * SVGTextElement est actuellement positionné par Apex.
           *
           * On utilise text-anchor="start" pour reproduire
           * le comportement flex horizontal.
           */
          text.setAttribute(
            'text-anchor',
            'start'
          );

          text.setAttribute(
            'x',
            String(textStartX)
          );

          /*
           * Centrage vertical du texte.
           *
           * dominant-baseline="middle" permet d'aligner le texte
           * avec le centre du point.
           */
          text.setAttribute(
            'y',
            String(centerY)
          );

          text.setAttribute(
            'dominant-baseline',
            'middle'
          );

          // ------------------------------------------------------------
          // Point
          // ------------------------------------------------------------

          const circle =
            document.createElementNS(
              'http://www.w3.org/2000/svg',
              'circle'
            );

          circle.setAttribute(
            'class',
            `chart-label-dot chart-label-dot-series-${seriesIndex}`
          );

          circle.setAttribute(
            'cx',
            String(dotCenterX)
          );

          circle.setAttribute(
            'cy',
            String(dotCenterY)
          );

          circle.setAttribute(
            'r',
            String(DOT_RADIUS)
          );

          circle.setAttribute(
            'fill',
            seriesColor
          );

          circle.setAttribute(
            'stroke',
            'none'
          );

          // Le point doit être devant le background,
          // mais avant le texte.
          text.parentNode?.insertBefore(
            circle,
            text
          );
        });
      });
    });
  };

  // --------------------------------------------------------------------
  // Chart
  // --------------------------------------------------------------------

  const chartOptions =
    useChart({
      chart: {
        stacked: true,

        animations: {
          enabled: true,
          speed: 500,

          animateGradually: {
            enabled: true,
            delay: 200,
          },

          dynamicAnimation: {
            enabled: true,
            speed: 300,
          },
        },

        events: {
          // ----------------------------------------------------------
          // First render
          // ----------------------------------------------------------

          mounted: () => {
            injectSeriesDotsIntoLabels();
          },

          // ----------------------------------------------------------
          // Any Apex redraw
          // ----------------------------------------------------------

          updated: () => {
            injectSeriesDotsIntoLabels();
          },

          // ----------------------------------------------------------
          // Hide/show series
          // ----------------------------------------------------------

          legendClick: (
            chartContext: any,
            seriesIndex: number,
            config: any
          ) => {
            const seriesName =
              config?.config
                ?.series?.[
                seriesIndex
              ]?.name;

            if (!seriesName) {
              return;
            }

            /**
             * Apex masque/réaffiche lui-même
             * le graphique.
             *
             * Nous synchronisons seulement
             * les annotations.
             */
            setHiddenSeries(
              (previous) => {
                const next =
                  new Set(
                    previous
                  );

                if (
                  next.has(
                    seriesName
                  )
                ) {
                  next.delete(
                    seriesName
                  );
                } else {
                  next.add(
                    seriesName
                  );
                }

                return next;
              }
            );
          },
        },
      },

      series,

      // ----------------------------------------------------------------
      // Stroke
      // ----------------------------------------------------------------

      stroke: {
        width: 0,
        show: false,
      },

      labels:
        labels || [],

      // ----------------------------------------------------------------
      // X Axis
      // ----------------------------------------------------------------

      xaxis: {
        labels: {
          offsetY: 4,
        },
      },

      // ----------------------------------------------------------------
      // Grid
      // ----------------------------------------------------------------

      grid: {
        padding: {
          top:
            showDataLabels
              ? 20
              : 0,

          bottom: 8,
        },
      },

      // ----------------------------------------------------------------
      // Legend
      // ----------------------------------------------------------------

      legend: {
        show:
          !hideLegend,

        itemMargin: {
          vertical: 8,
        },

        markers: {
          customHTML:
            function () {
              return `
                <div
                  style="
                    width: ${legendMarkerSize}px;
                    height: ${legendMarkerSize}px;
                    border-radius: 50%;
                    background-color: currentcolor;
                  "
                ></div>
              `;
            },
        },

        position: 'top',

        offsetY: 0,

        ...(legendLabelColor
          ? {
            labels: {
              colors:
                legendLabelColor,
            },
          }
          : {}),
      },

      // ----------------------------------------------------------------
      // Disable native dataLabels
      //
      // Tout passe par annotations.points.
      // ----------------------------------------------------------------

      dataLabels: {
        enabled: false,
      },

      // ----------------------------------------------------------------
      // Colors
      // ----------------------------------------------------------------

      fill: {
        colors:
          resolvedColors,
      },

      colors:
        resolvedColors,

      // ----------------------------------------------------------------
      // Bars
      // ----------------------------------------------------------------

      plotOptions: {
        bar: {
          barHeight: '80%',

          columnWidth: '70%',

          borderRadius:
            barBorderRadius,

          distributed: false,

          borderRadiusApplication:
            'end',

          borderRadiusWhenStacked:
            'last',
        },
      },

      // ----------------------------------------------------------------
      // Tooltip
      // ----------------------------------------------------------------

      tooltip: {
        shared: true,
        intersect: false,

        custom: function ({
          series,
          dataPointIndex,
          w,
        }) {
          if (
            !series ||
            !w.globals.seriesNames ||
            !w.globals.colors
          ) {
            return '';
          }

          const date =
            w.globals.labels[dataPointIndex] || '';

          const collapsed = [
            ...(w.globals.collapsedSeriesIndices || []),
            ...(w.globals.ancillaryCollapsedSeriesIndices || []),
          ];

          const rows = series
            .map((s: any, i: number) => {
              if (collapsed.includes(i)) {
                return '';
              }

              const value = s[dataPointIndex];

              const formattedValue =
                value != null && !isNaN(value)
                  ? formatValue(value)
                  : '0';

              return `
          <div
            style="
              display:flex;
              align-items:center;
              width:100%;
              box-sizing:border-box;
              padding:4px;
              gap:8px;
            "
          >
            <span
              style="
                display:inline-block;
                width:4px;
                height:20px;
                border-radius:12px;
                background-color:${w.globals.colors[i]};
                flex-shrink:0;
              "
            ></span>

            <div
              style="
                flex:1;
                min-width:0;
                display:flex;
                justify-content:space-between;
                align-items:center;
                font-family:Public Sans,sans-serif;
                font-size:13px;
                font-weight:500;
                color:#333C47;
                white-space:nowrap;
                gap:16px;
              "
            >
              <span>
                ${w.globals.seriesNames[i]}
              </span>

              <span
                style="
                  flex-shrink:0;
                  text-align:right;
                "
              >
                ${formattedValue}
              </span>
            </div>
          </div>
        `;
            })
            .join('');

          const pointValues =
            series.map(
              (s: any) =>
                Number(s?.[dataPointIndex]) || 0
            );

          const pointNames =
            w.globals.seriesNames || [];

          const extraRows =
            extraTooltipRows &&
              extraTooltipRows.length
              ? extraTooltipRows
                .map((r) => {
                  const value =
                    r.compute(
                      pointValues,
                      pointNames
                    );

                  const formattedValue =
                    value != null && !isNaN(value)
                      ? formatValue(value)
                      : '0';

                  return `
                <div
                  style="
                    display:flex;
                    align-items:center;
                    width:100%;
                    box-sizing:border-box;
                    padding:4px;
                    gap:8px;
                  "
                >
                  <span
                    style="
                      display:inline-block;
                      width:4px;
                      height:20px;
                      border-radius:12px;
                      background-color:${r.color};
                      flex-shrink:0;
                    "
                  ></span>

                  <div
                    style="
                      flex:1;
                      min-width:0;
                      display:flex;
                      justify-content:space-between;
                      align-items:center;
                      font-family:Public Sans,sans-serif;
                      font-size:13px;
                      font-weight:500;
                      color:#333C47;
                      white-space:nowrap;
                      gap:16px;
                    "
                  >
                    <span>
                      ${r.name}
                    </span>

                    <span
                      style="
                        flex-shrink:0;
                        text-align:right;
                      "
                    >
                      ${formattedValue}
                    </span>
                  </div>
                </div>
              `;
                })
                .join('')
              : '';

          return `
      <div
        style="
          width:320px;
          min-width:320px;
          max-width:320px;
          box-sizing:border-box;

          background:#FCFCFD;

          border:none;
          border-radius:12px;

          box-shadow:0px 4px 8px rgba(145,158,171,0.16);

          overflow:hidden;
        "
      >
        <div
          style="
            padding:12px 12px 4px 12px;
            box-sizing:border-box;
          "
        >
          <p
            style="
              font-family:Public Sans,sans-serif;
              color:#333C47;
              font-size:14px;
              font-weight:500;
              line-height:14px;
              white-space:nowrap;
              margin:0;
              margin-bottom: 12px
            "
          >
            ${date}
          </p>
        </div>

        <div
          style="
            padding:0 12px 12px 12px;
            display:flex;
            flex-direction:column;
            box-sizing:border-box;
          "
        >
          ${rows}
          ${extraRows}
        </div>
      </div>
    `;
        },

        y: {
          formatter: (
            val: number
          ) => {
            if (
              val == null ||
              isNaN(val)
            ) {
              return '0';
            }

            return formatValue(val);
          },
        },
      },

      // ----------------------------------------------------------------
      // Y Axis
      // ----------------------------------------------------------------

      yaxis: [
        {
          labels: {
            formatter(
              value
            ) {
              if (
                value == null ||
                isNaN(value)
              ) {
                return '0';
              }

              return formatValue(
                value
              );
            },
          },
        },
      ],

      // ----------------------------------------------------------------
      // Annotations
      // ----------------------------------------------------------------

      annotations: {
        xaxis:
          annotations,

        points:
          labelAnnotations,
      },
    });

  // --------------------------------------------------------------------

  return (
    <Box
      ref={
        chartContainerRef
      }
      sx={{
        width: '100%',

        'span .apexcharts-legend-marker':
        {
          borderRadius:
            100,
        },

        '& .chart-data-label':
        {
          filter:
            'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.04))',
        },
      }}
    >
      <Chart
        ref={chartRef}
        dir="ltr"
        type="bar"
        series={series}
        options={
          chartOptions
        }
        height={height}
      />
    </Box>
  );
}